// Procedural surface, camo and decal-atlas textures as shared THREE textures.
//
// Pixels are painted in a texture worker (textureWorker.ts) and arrive as ImageBitmaps; nothing here
// paints or reads pixels on the main thread, and when Worker / OffscreenCanvas do not exist the same
// painters run on the main thread in time slices. A getter returns its texture synchronously: the
// texture holds a 1 px placeholder until the bitmap arrives and is then filled in place, so every
// material that shares it updates at once. `texturesReady()` resolves when nothing is pending.
//
// Textures are cached by (kind, painted colour, grime level); callers must not dispose them, use
// disposeTextureCache(). The quality scale (setTextureScale) shrinks every texture in the worker
// before it is transferred, so a lower preset costs less memory and no extra main-thread work.

import * as THREE from "three";
import { Slicer } from "../yield";
import { camoHasTexture } from "./textureGen";
import { renderJob } from "./textureRender";
import type { CanvasLike } from "./textureRender";
import { CAMO_SIZE, DECAL_ATLAS_SIZE, scaledSize, surfaceSpec } from "./textureSpec";
import type { TextureJob, WorkerRequest, WorkerResponse } from "./textureSpec";
import { parseHex } from "./palette";
import type { CamoName, TextureKind } from "./types";

/** A map surface texture and how its material must use it. */
export interface SurfaceTexture {
  readonly map: THREE.Texture;
  /** Material colour ("#ffffff" when the colour is baked into the texture). */
  readonly tint: string;
  /** Linear multiplier on the material colour that compensates the shading painted into tinted textures. */
  readonly gain: number;
}

const MAX_WORKERS = 2;
/** A worker is shut down after this long without jobs. */
const WORKER_IDLE_MS = 8000;
/** Milliseconds of fallback painting between two yields. */
const FALLBACK_SLICE_MS = 8;

// ---------------------------------------------------------------------------
// Rendering back ends
// ---------------------------------------------------------------------------

function workersAvailable(): boolean {
  return typeof Worker !== "undefined" && typeof OffscreenCanvas !== "undefined" && typeof createImageBitmap === "function";
}

/** False in node (unit tests build materials but never draw): textures then keep their placeholder. */
function canPaint(): boolean {
  return typeof OffscreenCanvas !== "undefined" || typeof document !== "undefined";
}

function makeMainCanvas(size: number): CanvasLike {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(size, size);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  return canvas;
}

const fallbackSlicer = new Slicer(FALLBACK_SLICE_MS);
let fallbackChain: Promise<unknown> = Promise.resolve();

/** Paints on the main thread, one job at a time, yielding to the event loop between painter steps. */
function renderOnMainThread(job: TextureJob, outSize: number): Promise<ImageBitmap> {
  const run = fallbackChain.then(() => renderJob(job, outSize, makeMainCanvas, () => fallbackSlicer.turn()));
  fallbackChain = run.catch(() => undefined);
  return run;
}

interface Task {
  readonly id: number;
  readonly job: TextureJob;
  readonly outSize: number;
  readonly resolve: (bitmap: ImageBitmap) => void;
  readonly reject: (reason: Error) => void;
}

interface Slot {
  readonly worker: Worker;
  task: Task | null;
}

/** One or two module workers that paint jobs one at a time each. */
class WorkerPool {
  /** Set when a worker failed to start or crashed; every job then paints on the main thread. */
  broken = false;
  private readonly slots: Slot[] = [];
  private readonly queue: Task[] = [];
  private nextId = 1;
  private idleTimer = 0;

  render(job: TextureJob, outSize: number): Promise<ImageBitmap> {
    const { promise, resolve, reject } = Promise.withResolvers<ImageBitmap>();
    this.queue.push({ id: this.nextId++, job, outSize, resolve, reject });
    this.pump();
    return promise;
  }

  private maxWorkers(): number {
    return Math.min(MAX_WORKERS, Math.max(1, Math.floor((navigator.hardwareConcurrency || 2) / 2)));
  }

  private pump(): void {
    window.clearTimeout(this.idleTimer);
    while (this.queue.length > 0) {
      let slot = this.slots.find((s) => s.task === null);
      if (slot === undefined && this.slots.length < this.maxWorkers()) slot = this.spawn();
      if (slot === undefined) return;
      const task = this.queue.shift()!;
      slot.task = task;
      const request: WorkerRequest = { id: task.id, job: task.job, outSize: task.outSize };
      slot.worker.postMessage(request);
    }
    if (this.slots.every((s) => s.task === null)) {
      this.idleTimer = window.setTimeout(() => this.shutdown(), WORKER_IDLE_MS);
    }
  }

  private spawn(): Slot {
    const worker = new Worker(new URL("./textureWorker.ts", import.meta.url), { type: "module" });
    const slot: Slot = { worker, task: null };
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const task = slot.task;
      slot.task = null;
      const msg = event.data;
      if (task !== null && task.id === msg.id) {
        if ("bitmap" in msg) task.resolve(msg.bitmap);
        else task.reject(new Error(msg.error));
      }
      this.pump();
    };
    worker.onerror = (event) => {
      console.warn("textures: worker failed, painting on the main thread", event.message);
      this.fail();
    };
    this.slots.push(slot);
    return slot;
  }

  private fail(): void {
    this.broken = true;
    const pending = [...this.slots.map((s) => s.task).filter((t): t is Task => t !== null), ...this.queue];
    this.queue.length = 0;
    this.shutdown();
    for (const task of pending) task.reject(new Error("texture worker failed"));
  }

  private shutdown(): void {
    window.clearTimeout(this.idleTimer);
    for (const slot of this.slots) slot.worker.terminate();
    this.slots.length = 0;
  }
}

let pool: WorkerPool | null = null;

/** Paints a job in a worker (main thread when workers are unavailable or broken). */
async function render(job: TextureJob, outSize: number): Promise<ImageBitmap> {
  if (!workersAvailable()) return renderOnMainThread(job, outSize);
  pool ??= new WorkerPool();
  if (pool.broken) return renderOnMainThread(job, outSize);
  try {
    return await pool.render(job, outSize);
  } catch (err) {
    if (!pool.broken) throw err;
    return renderOnMainThread(job, outSize);
  }
}

// ---------------------------------------------------------------------------
// Texture cache
// ---------------------------------------------------------------------------

interface Entry {
  readonly tex: THREE.Texture;
  readonly job: TextureJob;
  /** Edge of the bitmap the latest request asked for (0 = none yet). */
  requested: number;
  /** Counts requests; a bitmap is applied only when it answers the latest one. */
  seq: number;
}

const cache = new Map<string, Entry>();
const inflight = new Set<Promise<void>>();
let scale = 1;

function hasAlpha(job: TextureJob): boolean {
  return job.type === "decals" || (job.type === "surface" && job.kind === "grate");
}

/** A 1 x 1 stand-in so the texture is valid before its bitmap arrives. */
function placeholder(job: TextureJob): ImageData {
  const c = job.type === "surface" ? parseHex(job.paint) : { r: 128, g: 128, b: 128 };
  const data = new Uint8ClampedArray([c.r, c.g, c.b, hasAlpha(job) ? 0 : 255]);
  // Without ImageData (node) nothing is ever uploaded, so a plain pixel record does.
  return typeof ImageData === "undefined" ? ({ data, width: 1, height: 1 } as ImageData) : new ImageData(data, 1, 1);
}

function createEntry(job: TextureJob, wrap: THREE.Wrapping): Entry {
  const tex = new THREE.Texture(placeholder(job));
  tex.name = job.key;
  tex.wrapS = wrap;
  tex.wrapT = wrap;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  // The bitmaps are flipped and un-premultiplied when they are made (textureRender.ts); three's own
  // flip / premultiply upload flags do not apply to ImageBitmaps.
  tex.flipY = false;
  tex.premultiplyAlpha = false;
  tex.needsUpdate = true;
  const entry: Entry = { tex, job, requested: 0, seq: 0 };
  cache.set(job.key, entry);
  request(entry);
  return entry;
}

function applyBitmap(entry: Entry, bitmap: ImageBitmap): void {
  const { tex } = entry;
  const old = tex.image as ImageData | ImageBitmap;
  // Texture storage is immutable: a different size needs a new GPU texture.
  if (old.width !== bitmap.width || old.height !== bitmap.height) tex.dispose();
  tex.image = bitmap;
  tex.needsUpdate = true;
  if (typeof ImageBitmap !== "undefined" && old instanceof ImageBitmap) old.close();
}

/** (Re)paints the entry at the current quality scale unless that is already what it shows or awaits. */
function request(entry: Entry): void {
  if (!canPaint()) return;
  const outSize = scaledSize(entry.job.size, scale);
  if (outSize === entry.requested) return;
  entry.requested = outSize;
  const seq = ++entry.seq;
  const task: Promise<void> = render(entry.job, outSize)
    .then(
      (bitmap) => {
        if (cache.get(entry.job.key) !== entry || seq !== entry.seq) bitmap.close();
        else applyBitmap(entry, bitmap);
      },
      (err: unknown) => console.warn(`textures: ${entry.job.key} failed`, err),
    )
    .finally(() => inflight.delete(task));
  inflight.add(task);
}

function acquire(job: TextureJob, wrap: THREE.Wrapping = THREE.RepeatWrapping): THREE.Texture {
  return (cache.get(job.key) ?? createEntry(job, wrap)).tex;
}

/**
 * Seamless tiling texture of `kind`, shared by every material that resolves to the same (kind,
 * colour, grime level). Grime is quantised to 0, .5 and 1. Plain, concrete and tiles are painted
 * neutral and tinted by the material (`tint` x `gain`); the other kinds have the colour baked in.
 */
export function getSurfaceTexture(kind: TextureKind, color: string, grime = 0): SurfaceTexture {
  const { job, tint, gain } = surfaceSpec(kind, color, grime);
  return { map: acquire(job), tint, gain };
}

/** Seamless 256 px camo texture; null for "none" and "gold", which are plain colours. */
export function getCamoTexture(camo: CamoName): THREE.Texture | null {
  if (!camoHasTexture(camo)) return null;
  return acquire({ type: "camo", camo, key: `camo|${camo}`, size: CAMO_SIZE });
}

/** The static decal atlas (transparent, straight alpha, clamped); see decalKinds.ts for its layout. */
export function getDecalAtlas(): THREE.Texture {
  return acquire({ type: "decals", key: "decals", size: DECAL_ATLAS_SIZE }, THREE.ClampToEdgeWrapping);
}

/**
 * Sets the quality scale (0..1, each dimension) of every texture. Textures that already exist are
 * repainted at the new size in the background and swapped when ready; new ones start at it.
 */
export function setTextureScale(next: number): void {
  const clamped = Math.min(1, Math.max(0.25, next));
  if (clamped === scale) return;
  scale = clamped;
  for (const entry of cache.values()) request(entry);
}

/** Resolves once every texture requested so far has its final image (also those requested meanwhile). */
export async function texturesReady(): Promise<void> {
  while (inflight.size > 0) await Promise.all([...inflight]);
}

/** Disposes every cached texture (GPU memory) and empties the cache; results still in flight are dropped. */
export function disposeTextureCache(): void {
  for (const { tex } of cache.values()) {
    const image = tex.image as ImageData | ImageBitmap;
    tex.dispose();
    if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) image.close();
  }
  cache.clear();
}

/** True when the texture of this kind has transparent pixels (needs alphaTest on the material). */
export function textureHasAlpha(kind: TextureKind): boolean {
  return kind === "grate";
}
